/* eslint-disable react-refresh/only-export-components */
import { selMessage } from "@selector/shared";
import { uniq } from "ramda";
import { NotificationIntent } from "widgets";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { PubSubActions } from "@/global/pubsub-actions.js";
import type { Notification } from "@/graphql.ts";
import { FileDownloadStatus, FileScannerStatus, SubscriptionTypeEnum } from "@/modules/notifications/utils/types.ts";
import {
  ALERT_DISPLAYED,
  ALERTS_SUBSCRIPTION_FAILURE,
  ALERTS_SUBSCRIPTION_START,
  ALERTS_SUBSCRIPTION_STOP,
  FILE_DOWNLOADS_SUBSCRIPTION_FAILURE,
  FILE_DOWNLOADS_SUBSCRIPTION_START,
  FILE_DOWNLOADS_SUBSCRIPTION_STOP,
  FILE_MESSAGES_SUBSCRIPTION_FAILURE,
  FILE_MESSAGES_SUBSCRIPTION_START,
  FILE_MESSAGES_SUBSCRIPTION_STOP,
  FILE_SCANNER_SUBSCRIPTION_START,
  FILE_SCANNER_SUBSCRIPTION_STOP,
  LOAD_NOTIFICATION_FAILURE,
  LOAD_NOTIFICATION_SUCCESS,
  LOAD_NOTIFICATIONS_FAILURE,
  LOAD_NOTIFICATIONS_REQUEST,
  LOAD_NOTIFICATIONS_SUCCESS,
  MARK_ALL_NOTIFICATIONS_AS_READ_SUCCESS,
  MARK_NOTIFICATION_AS_READ_SUCCESS,
  MESSAGES_SUBSCRIPTION_START,
  MESSAGES_SUBSCRIPTION_STOP,
  NOTIFICATION_SUBSCRIPTION_FAILURE,
  NOTIFICATION_SUBSCRIPTION_START,
  NOTIFICATION_SUBSCRIPTION_STOP,
  SET_NOTIFICATION_AS_READ_FAILURE,
} from "@/redux/action-types.ts";
import { READ_NOTIFICATIONS_LIMIT } from "@/settings/settings.ts";
import { webSocketClient } from "@/tools.js";
import { FileDownloadNotification } from "@/ui-modules/notifications/FileDownloadNotification.tsx";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import type { IAppState } from "../reducer.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";
import { notifyIndexingStatusChange } from "./files.js";

/**
 * State definition
 */
export interface INotificationsState {
  notifications: Notification[];
  lastNotification: Notification | null; // will allow any component to watch for new notification and react accordingly
  isLoading: boolean;
  notificationsSubscriptionId: string | null;
  fileDownloadsSubscriptionId: string | null; // subscription identifiers do not need to be in redux, we should refactor and put all this logic and data in WebApiClient
  fileScannerSubscriptionId: string | null;
  alertsSubscriptionId: string | null;
  messagesSubscriptionId: string | null;
  fileSubscriptionId: string | null;
}

const initialState: INotificationsState = {
  notifications: [],
  lastNotification: null,
  isLoading: false,
  notificationsSubscriptionId: null,
  fileDownloadsSubscriptionId: null,
  fileScannerSubscriptionId: null,
  alertsSubscriptionId: null,
  messagesSubscriptionId: null,
  fileSubscriptionId: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_NOTIFICATIONS_REQUEST]: (state: INotificationsState): INotificationsState => {
    return { ...state, isLoading: true };
  },
  [LOAD_NOTIFICATIONS_SUCCESS]: (state: INotificationsState, payload: Notification[]): INotificationsState => {
    // FIXME Temporary solution to prevent customers to see potentially thousands of identical
    //  notification. Since we cannot reproduce it yet we simply filter out duplicates in production mode.
    //  See SEL9-532 and SEL9-823 for context
    if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
      const removedDuplicates = uniq(payload);
      if (payload.length !== removedDuplicates.length) {
        console.warn("Notifications list contains %s duplicates", payload.length - removedDuplicates.length);
      }
    }

    return {
      ...state,
      // prevent duplicated notifications in production mode, see SEL9-532
      notifications: import.meta.env.DEV || import.meta.env.MODE === "staging" ? payload : uniq(payload),
      isLoading: false,
    };
  },
  [LOAD_NOTIFICATIONS_FAILURE]: (state: INotificationsState): INotificationsState => {
    return { ...state, isLoading: false };
  },
  [NOTIFICATION_SUBSCRIPTION_START]: (state: INotificationsState, payload: number): INotificationsState => {
    return { ...state, notificationsSubscriptionId: String(payload) };
  },
  [NOTIFICATION_SUBSCRIPTION_STOP]: (state: INotificationsState): INotificationsState => {
    return { ...state, notificationsSubscriptionId: null };
  },
  [MARK_NOTIFICATION_AS_READ_SUCCESS]: (state: INotificationsState, notificationId: number): INotificationsState => {
    const newNotifications: Notification[] = state.notifications.map((notification: Notification) => {
      return notification.id === notificationId ? { ...notification, read: true } : notification;
    });
    return { ...state, notifications: newNotifications };
  },
  [MARK_ALL_NOTIFICATIONS_AS_READ_SUCCESS]: (state: INotificationsState): INotificationsState => {
    const newNotifications: Notification[] = state.notifications.map((notification: Notification) => {
      return { ...notification, read: true };
    });
    return { ...state, notifications: newNotifications };
  },
  [LOAD_NOTIFICATION_SUCCESS]: (state: INotificationsState, payload: Notification): INotificationsState => {
    const updatedNotifications: Notification[] = state.notifications.slice(0);
    updatedNotifications.unshift(payload);
    return { ...state, notifications: updatedNotifications, lastNotification: payload };
  },

  [FILE_DOWNLOADS_SUBSCRIPTION_START]: (state: INotificationsState, payload: string): INotificationsState => {
    return { ...state, fileDownloadsSubscriptionId: payload };
  },
  [FILE_DOWNLOADS_SUBSCRIPTION_STOP]: (state: INotificationsState): INotificationsState => {
    return { ...state, fileDownloadsSubscriptionId: null };
  },
  [ALERTS_SUBSCRIPTION_START]: (state: INotificationsState, payload: string): INotificationsState => {
    return { ...state, alertsSubscriptionId: payload };
  },
  [ALERTS_SUBSCRIPTION_STOP]: (state: INotificationsState): INotificationsState => {
    return { ...state, alertsSubscriptionId: null };
  },
  [MESSAGES_SUBSCRIPTION_START]: (state: INotificationsState, payload: string): INotificationsState => {
    return { ...state, messagesSubscriptionId: payload };
  },
  [MESSAGES_SUBSCRIPTION_STOP]: (state: INotificationsState): INotificationsState => {
    return { ...state, messagesSubscriptionId: null };
  },

  [FILE_MESSAGES_SUBSCRIPTION_START]: (state: INotificationsState, payload: string): INotificationsState => {
    return { ...state, fileSubscriptionId: payload };
  },
  [FILE_MESSAGES_SUBSCRIPTION_STOP]: (state: INotificationsState): INotificationsState => {
    return { ...state, fileSubscriptionId: null };
  },

  [FILE_SCANNER_SUBSCRIPTION_START]: (state: INotificationsState, payload: string): INotificationsState => {
    return { ...state, fileScannerSubscriptionId: payload };
  },
  [FILE_SCANNER_SUBSCRIPTION_STOP]: (state: INotificationsState): INotificationsState => {
    return { ...state, fileScannerSubscriptionId: null };
  },
});

/**
 * Action creators
 */
export function loadNotificationsList(): ReduxAction<void> {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_NOTIFICATIONS_REQUEST });

    return webApiClient
      .fetchNotificationList(READ_NOTIFICATIONS_LIMIT)
      .then((response: Notification[]) => {
        dispatch({
          type: LOAD_NOTIFICATIONS_SUCCESS,
          payload: response,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_NOTIFICATIONS_FAILURE,
          alertTitle: "Fetching of Notifications Failed",
        }),
      );
  };
}

export function loadSingleNotification(notificationId: number): ReduxAction<void> {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    return webApiClient
      .fetchNotification(notificationId)
      .then((notification: Notification) => {
        dispatch({
          type: LOAD_NOTIFICATION_SUCCESS,
          payload: notification,
        });
        notify.fromNotification(notification, { toastId: notification.id, markAsReadOverInteraction: true });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_NOTIFICATION_FAILURE,
          alertTitle: `Fetching of Notification ${notificationId} Failed`,
        }),
      );
  };
}

export function markAllAsRead(): ReduxAction<void> {
  return (dispatch) => {
    //    const state = getState().notifications;
    //    const unreadNotifications: Array<INotification> = state.notifications.filter((notification) => !notification.read);
    //    Note that we reload the notifications list only at the very end to avoid race conditions
    //    unreadNotifications.forEach((notification: INotification, index: number) => {
    //      dispatch(markAsRead(notification.id, index === unreadNotifications.length - 1));
    //    });
    dispatch(markAllNotificationsAsRead(true));
  };
}

export function markAsRead(notificationId: number, reloadList = true): ReduxAction<void> {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient
      .setNotificationAsRead(notificationId)
      .then((notificationWasUpdated: boolean) => {
        if (notificationWasUpdated) {
          // optimistic update of local notifications
          dispatch({
            type: MARK_NOTIFICATION_AS_READ_SUCCESS,
            payload: notificationId,
          });
          // reload the notifications list because the optimistic update may not succeed because the
          // notification is not yet in the list
          if (reloadList) {
            dispatch(loadNotificationsList());
          }
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SET_NOTIFICATION_AS_READ_FAILURE,
          alertTitle: `Could Not Set Notification ${notificationId} as Read`,
        }),
      );
  };
}

export function markAllNotificationsAsRead(reloadList = true): ReduxAction<void> {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient
      .setAllNotificationsAsRead()
      .then((_notificationWasUpdated: boolean) => {
        // we don't care about the resulting boolean, because the user might set the notifs as read in another tab, and we still want to get rid of the "unread" badge in this one
        dispatch({
          type: MARK_ALL_NOTIFICATIONS_AS_READ_SUCCESS,
          payload: null,
        });
        // reload the notifications list because the optimistic update may not succeed because the
        // notification is not yet in the list
        if (reloadList) {
          dispatch(loadNotificationsList());
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SET_NOTIFICATION_AS_READ_FAILURE,
          alertTitle: "Could Not Set Unread Notifications as Read",
        }),
      );
  };
}

export function watchAllInfoChannels(): ReduxAction<void> {
  return (dispatch) => {
    dispatch(watchNotifications()); // notifications that are shown in the Notif Panel
    dispatch(watchFileDownloads()); // file download is another kind of notification
    dispatch(watchFileScanner()); // File scanner notifications
    dispatch(watchAlerts()); // errors and exceptions triggered from MT
    dispatch(watchFileMessages()); // generic messages from MT
  };
}

export function unwatchAllInfoChannels(): ReduxAction<void> {
  return (dispatch) => {
    dispatch(unwatchNotifications()); // notifications that are shown in the Notif Panel
    dispatch(unwatchFileDownloads()); // file download is another kind of notification
    dispatch(unwatchFileScanner());
    dispatch(unwatchAlerts());
    dispatch(unwatchFileMessages());
  };
}

export function watchNotifications(): ReduxAction<void> {
  return (dispatch) => {
    const subscriptionId = webSocketClient.subscribe({
      subscription: { eventType: SubscriptionTypeEnum.Notification },
      onMessage(message) {
        dispatch(loadSingleNotification(message.notificationId));
      },
      onError: createErrorHandler(dispatch, {
        actionType: NOTIFICATION_SUBSCRIPTION_FAILURE,
        alertTitle: "Subscription to Notifications Failed",
        alertMessage: "Live notifications won't be active",
        showAlert: false,
      }) as unknown as (error: Error) => void,
    });

    if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
      console.warn(
        "Watching notifications with NOTIFICATION_SUBSCRIPTION_START with subscription ID '%s'",
        subscriptionId,
      );
    }
    dispatch({
      type: NOTIFICATION_SUBSCRIPTION_START,
      payload: subscriptionId,
    });
  };
}

export function unwatchNotifications(): ReduxAction<void> {
  return (dispatch, getState) => {
    const subscriptionId = getState().notifications.notificationsSubscriptionId;
    if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
      console.warn(
        "Unwatching notifications with NOTIFICATION_SUBSCRIPTION_STOP with subscription ID '%s'",
        subscriptionId,
      );
    }
    if (subscriptionId) {
      webSocketClient.unsubscribe(subscriptionId);
    }
    dispatch({
      type: NOTIFICATION_SUBSCRIPTION_STOP,
    });
  };
}

export function watchFileDownloads(): ReduxAction<void> {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    const subscriptionId = webSocketClient.subscribe({
      subscription: { eventType: SubscriptionTypeEnum.FileDownload },
      onMessage(message) {
        // the download/export procedure involves a first REST query that is executed directly using a POSTed request via a hidden iframe,
        // and we have no way to get the request response, this is why we use a parallel websocket channel to
        // provide the response by artificially storing and resolving/rejecting a Promise

        const fileKey = message.fileKey;
        const status = message.status;
        const downloadPromise = webApiClient.getFileDownloadPromise(fileKey);

        if (downloadPromise) {
          if (message.status === FileDownloadStatus.PREPARING) {
            webApiClient.updateFileDownloadStatus(fileKey, message.status);
          } else {
            webApiClient.unwatchFileDownload(fileKey);
          }

          if (status === FileDownloadStatus.ERROR || status === FileDownloadStatus.INVALID) {
            // meaning there is an error and the download could not be started
            if (downloadPromise.status === FileDownloadStatus.PREPARING) {
              // case where there is no more dialog, but perhaps an alert
              notify.update(
                message.fileKey,
                {
                  title: "File Preparation Failed",
                  children: (
                    <>
                      <div>The file {message.fileName} could not be exported.</div>
                      <div>{message.message}</div>
                    </>
                  ),
                },
                NotificationIntent.Error,
                { toastId: message.fileKey, autoClose: false, markAsReadOverInteraction: true },
              );
            } else {
              // REJECT the promise expected by the original form, so that the error can be shown in the form footer
              downloadPromise.reject(new Error(`${message.message}`));
            }
          } else {
            // in all other cases, we resolve the promise expected by the original form (if any), and therefore terminating the visual pending
            downloadPromise.resolve(true);
          }
        }

        // the file was not ready on time, we make this download asynchronous
        // With the new post system, we do not have a downloadPromise (legacy)
        if (message.status === FileDownloadStatus.PREPARING) {
          notify.info(
            {
              title: "Preparing File",
              children: selMessage({ id: "websocket.file.delayedExport" }),
            },
            { toastId: message.fileKey, autoClose: false, markAsReadOverInteraction: true },
          );
        }

        // the async download is finished, this will be visible also if the application was closed in the meantime,
        // but will appear in all application instances
        if (message.status === FileDownloadStatus.READY) {
          notify.update(
            message.fileKey,
            {
              title: "File Ready",
              children: <FileDownloadNotification downloadEvent={message} />,
            },
            NotificationIntent.Success,
            { toastId: message.fileKey, autoClose: false, markAsReadOverInteraction: true },
          );
        }
      },
      onError: createErrorHandler(dispatch, {
        actionType: FILE_DOWNLOADS_SUBSCRIPTION_FAILURE,
        showAlert: false,
        throwError: true,
      }) as unknown as (error: Error) => void,
    });

    dispatch({
      type: FILE_DOWNLOADS_SUBSCRIPTION_START,
      payload: subscriptionId,
    });
  };
}

export function unwatchFileDownloads(): ReduxAction<void> {
  return (dispatch, getState) => {
    const subscriptionId = getState().notifications.fileDownloadsSubscriptionId;
    if (subscriptionId) {
      webSocketClient.unsubscribe(subscriptionId);
    }

    dispatch({
      type: FILE_DOWNLOADS_SUBSCRIPTION_STOP,
    });
  };
}

export function watchFileScanner(): ReduxAction<void> {
  return (dispatch) => {
    const subscriptionId = webSocketClient.subscribe({
      subscription: { eventType: SubscriptionTypeEnum.FileScanner },
      onMessage(event) {
        const status = event.status;

        switch (status) {
          case FileScannerStatus.STALE_SCAN_SUCCESS:
            notify.success({
              title: "Report generation succeeded",
              children: event.message,
            });
            break;
          case FileScannerStatus.STALE_SCAN_FAILED:
            notify.error({
              title: "Report generation failed",
              children: event.message,
            });
            break;
          case FileScannerStatus.DELETION_SUCCESS:
            notify.success({
              title: "Purge succeeded",
              children: event.message,
            });
            break;
          case FileScannerStatus.DELETION_WARNING:
            notify.warning({
              title: "Purge partially failed",
              children: event.message,
            });
            break;
          case FileScannerStatus.DELETION_FAILED:
            notify.error({
              title: "Purge failed",
              children: event.message,
            });
            break;
        }
      },
      onError: () => () => {
        notify.error({
          title: "An internal server error occurred",
        });
      },
    });

    dispatch({
      type: FILE_SCANNER_SUBSCRIPTION_START,
      payload: subscriptionId,
    });
  };
}

export function unwatchFileScanner(): ReduxAction<void> {
  return (dispatch, getState) => {
    const subscriptionId = getState().notifications.fileScannerSubscriptionId;
    if (subscriptionId) {
      webSocketClient.unsubscribe(subscriptionId);
    }

    dispatch({
      type: FILE_SCANNER_SUBSCRIPTION_STOP,
    });
  };
}

export function watchAlerts(): ReduxAction<void> {
  return (dispatch) => {
    const subscriptionId = webSocketClient.subscribe({
      subscription: { eventType: SubscriptionTypeEnum.Alert },
      onMessage(message) {
        // here we might discriminate between different kinds of errors or warning
        notify.error({
          title: selMessage({ id: "notifications.watchAlerts.errorTitle" }),
          children: message.message,
        });

        if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
          console.warn("Watch alerts with ALERTS_SUBSCRIPTION_FAILURE with subscription ID '%s'", subscriptionId);
        }

        dispatch({
          type: ALERT_DISPLAYED,
          payload: null,
          error: {
            message: message.message,
            stacktrace: message.exception.stackTrace,
          },
        });
      },
      onError: createErrorHandler(dispatch, {
        actionType: ALERTS_SUBSCRIPTION_FAILURE,
        showAlert: false,
        throwError: true,
      }) as unknown as (error: Error) => void,
    });

    dispatch({
      type: ALERTS_SUBSCRIPTION_START,
      payload: subscriptionId,
    });
  };
}

export function unwatchAlerts(): ReduxAction<void> {
  return (dispatch, getState) => {
    const subscriptionId = getState().notifications.alertsSubscriptionId;
    if (subscriptionId) {
      webSocketClient.unsubscribe(subscriptionId);
    }

    if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
      console.warn(
        "Watch alerts with ALERTS_SUBSCRIPTION_STOP with subscription ID '%s'",
        getState().notifications.alertsSubscriptionId,
      );
    }

    dispatch({
      type: ALERTS_SUBSCRIPTION_STOP,
    });
  };
}

export function watchFileMessages(): ReduxAction<void> {
  return (dispatch) => {
    const subscriptionId = webSocketClient.subscribe({
      subscription: { eventType: SubscriptionTypeEnum.File },
      onMessage(message) {
        PubSub.publish(PubSubActions.FILE, message);
        dispatch(notifyIndexingStatusChange(message));
      },
      onError: createErrorHandler(dispatch, {
        actionType: FILE_MESSAGES_SUBSCRIPTION_FAILURE,
        showAlert: false,
        throwError: true,
      }) as unknown as (error: Error) => void,
    });

    dispatch({
      type: FILE_MESSAGES_SUBSCRIPTION_START,
      payload: subscriptionId,
    });
  };
}

export function unwatchFileMessages(): ReduxAction<void> {
  return (dispatch, getState) => {
    const subscriptionId = getState().notifications.fileSubscriptionId;
    if (subscriptionId) {
      webSocketClient.unsubscribe(subscriptionId);
    }

    dispatch({
      type: FILE_MESSAGES_SUBSCRIPTION_STOP,
    });
  };
}
